import React, { useRef, useEffect, useCallback } from 'react';
import Button from '../ui/Button';
import { getForecastDayKind } from '../../utils/whosIn/canadaHolidays';

const SCROLL_STEP_RATIO = 0.85;

function formatForecastDate(isoDate) {
  const [y, m, d] = isoDate.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  return {
    dayOfWeek: dt.toLocaleDateString('en-US', { weekday: 'short' }),
    label: dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
  };
}

function normalizeDateStr(day) {
  return typeof day.schedule_date === 'string'
    ? day.schedule_date.slice(0, 10)
    : String(day.schedule_date);
}

const WhosInForecastTimeline = ({
  forecast,
  viewDate,
  onViewDateChange,
  onRefresh,
  refreshing,
  holidayMap,
  todayIso,
  loading,
  onSelectDate,
  forecastRange,
}) => {
  const scrollRef = useRef(null);
  const dayRefs = useRef({});

  const scrollByViewport = useCallback((direction) => {
    const el = scrollRef.current;
    if (!el) return;
    const step = el.clientWidth * SCROLL_STEP_RATIO * direction;
    el.scrollBy({ left: step, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    const scrollTarget = dayRefs.current[viewDate] || dayRefs.current[todayIso];
    if (scrollTarget?.scrollIntoView) {
      scrollTarget.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  }, [viewDate, todayIso, forecast.length]);

  return (
    <div className="whos-in-forecast-card-inner">
      <div className="whos-in-forecast-toolbar">
        <div className="whos-in-forecast-toolbar-left">
          <h2>Travel forecast</h2>
          {forecastRange && (
            <span className="whos-in-forecast-range-label">
              {forecastRange.from} — {forecastRange.to} ({forecastRange.span} days)
            </span>
          )}
        </div>
        <div className="whos-in-forecast-toolbar-right">
          <label htmlFor="whosin-forecast-date" className="whos-in-forecast-date-label">
            View date
            <input
              id="whosin-forecast-date"
              type="date"
              value={viewDate}
              onChange={(e) => onViewDateChange(e.target.value)}
            />
          </label>
          {onRefresh && (
            <Button onClick={onRefresh} variant="primary" disabled={refreshing} size="sm">
              {refreshing ? 'Loading…' : 'Refresh'}
            </Button>
          )}
        </div>
      </div>

      <p className="hint whos-in-forecast-hint">
        Click a day to select it — stats and roster below update for that date.
      </p>

      <div className="whos-in-forecast-legend">
        <span className="whos-in-fl-item whos-in-fl-today">Today</span>
        <span className="whos-in-fl-item whos-in-fl-view">View date</span>
        <span className="whos-in-fl-item whos-in-fl-weekend">Weekend</span>
        <span className="whos-in-fl-item whos-in-fl-holiday">Stat holiday</span>
      </div>

      {forecast.length === 0 ? (
        <p className="timeline-empty">{loading ? 'Loading forecast…' : 'No forecast data available.'}</p>
      ) : (
        <div className="whos-in-forecast-scroll-wrap">
          <button
            type="button"
            className="whos-in-forecast-arrow whos-in-forecast-arrow-left"
            onClick={() => scrollByViewport(-1)}
            aria-label="Scroll forecast left"
          >
            ‹
          </button>
          <div className="whos-in-forecast-container" ref={scrollRef}>
            {forecast.map((day) => {
              const dateStr = normalizeDateStr(day);
              const { dayOfWeek, label } = formatForecastDate(dateStr);
              const isToday = dateStr === todayIso;
              const isViewDate = dateStr === viewDate;
              const dayKind = getForecastDayKind(dateStr, holidayMap);
              const movement = (day.inbound_count || 0) + (day.outbound_count || 0);
              const dotSize = Math.min(20 + movement * 4, 56);

              const itemClass = [
                'whos-in-forecast-item',
                isToday ? 'today' : '',
                isViewDate ? 'view-date selected' : '',
                dayKind.kind === 'weekend' ? 'weekend' : '',
                dayKind.kind === 'holiday' ? 'holiday' : '',
              ]
                .filter(Boolean)
                .join(' ');

              const title = dayKind.holidayName
                ? `${dayKind.holidayName} — In ${day.inbound_count ?? 0}, Out ${day.outbound_count ?? 0}`
                : `In ${day.inbound_count ?? 0} inbound, Out ${day.outbound_count ?? 0} outbound`;

              return (
                <button
                  key={dateStr}
                  ref={(el) => {
                    dayRefs.current[dateStr] = el;
                  }}
                  type="button"
                  className={itemClass}
                  onClick={() => onSelectDate(dateStr)}
                  title={title}
                  aria-pressed={isViewDate}
                  aria-label={`${label}, ${dayOfWeek}${isViewDate ? ', selected' : ''}`}
                >
                  {isViewDate && (
                    <span className="whos-in-forecast-selected-badge">Selected</span>
                  )}
                  <div className="whos-in-forecast-date">
                    <span className="whos-in-forecast-dow">{dayOfWeek}</span>
                    <span className="whos-in-forecast-label">{label}</span>
                    {dayKind.holidayName && (
                      <span className="whos-in-forecast-holiday-name">{dayKind.holidayName}</span>
                    )}
                  </div>
                  <div
                    className="whos-in-forecast-dot"
                    style={{ width: `${dotSize}px`, height: `${dotSize}px` }}
                  >
                    <span>{day.travel_count ?? 0}</span>
                  </div>
                  <div className="whos-in-forecast-counts">
                    <span className="whos-in-fc-onsite" title="On site">
                      {day.on_site_count ?? 0}
                    </span>
                    <span className="whos-in-fc-in" title="Inbound">
                      In {day.inbound_count ?? 0}
                    </span>
                    <span className="whos-in-fc-out" title="Outbound">
                      Out {day.outbound_count ?? 0}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
          <button
            type="button"
            className="whos-in-forecast-arrow whos-in-forecast-arrow-right"
            onClick={() => scrollByViewport(1)}
            aria-label="Scroll forecast right"
          >
            ›
          </button>
        </div>
      )}
    </div>
  );
};

export default WhosInForecastTimeline;
